import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "relative inline-flex items-center justify-center gap-2 whitespace-nowrap select-none",
    "rounded-lg text-sm font-medium tracking-[-0.005em]",
    "transition-[background-color,border-color,color,box-shadow,transform,filter] duration-200 ease-out",
    "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/70 focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
    "disabled:pointer-events-none disabled:opacity-45 active:translate-y-px",
    "[&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        // Metallic fill: a lift tone at the top edge, the brand hue beneath,
        // and an inset highlight so it reads as a physical key, not a flat chip.
        primary: [
          "text-primary-foreground font-semibold",
          "bg-[linear-gradient(180deg,hsl(var(--accent))_0%,hsl(var(--primary))_62%)]",
          "shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.35),inset_0_-1px_0_0_hsl(0_0%_0%/0.18),0_1px_2px_0_hsl(var(--shadow-color)/0.3),0_8px_20px_-10px_hsl(var(--primary)/0.65)]",
          "hover:brightness-[1.06] hover:shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.4),inset_0_-1px_0_0_hsl(0_0%_0%/0.18),0_1px_2px_0_hsl(var(--shadow-color)/0.3),0_12px_28px_-10px_hsl(var(--primary)/0.8)]",
        ].join(" "),
        outline:
          "border border-border-strong bg-bg-elevated/40 text-fg shadow-[inset_0_1px_0_0_hsl(var(--hairline))] hover:bg-bg-elevated hover:border-primary/45",
        ghost: "bg-transparent text-fg-muted hover:text-fg hover:bg-fg/[0.06]",
        soft: "bg-primary/12 text-primary ring-1 ring-inset ring-primary/20 hover:bg-primary/18 hover:ring-primary/35",
        danger:
          "bg-danger text-white shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.25),0_8px_20px_-10px_hsl(var(--danger)/0.7)] hover:brightness-110",
        success:
          "bg-success text-white shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.25),0_8px_20px_-10px_hsl(var(--success)/0.7)] hover:brightness-110",
        link: "text-primary underline-offset-4 hover:underline px-0 h-auto",
      },
      size: {
        xs: "h-7 px-2.5 text-xs rounded-md [&_svg]:size-3.5",
        sm: "h-8 px-3 text-xs [&_svg]:size-3.5",
        md: "h-10 px-4 [&_svg]:size-4",
        lg: "h-12 px-6 text-[15px] rounded-xl [&_svg]:size-[18px]",
        icon: "h-10 w-10 [&_svg]:size-[18px]",
        "icon-sm": "h-8 w-8 rounded-md [&_svg]:size-4",
      },
    },
    compoundVariants: [{ variant: "link", className: "h-auto px-0" }],
    defaultVariants: { variant: "primary", size: "md" },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, loading, children, disabled, ...props }, ref) => {
    // When asChild is true, Slot requires exactly ONE React child. Pass through
    // the user's element directly; the loading spinner is only meaningful for
    // the native <button> branch.
    if (asChild) {
      return (
        <Slot ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props}>
          {children}
        </Slot>
      );
    }
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && (
          <span className="h-3.5 w-3.5 rounded-full border-2 border-current border-r-transparent animate-spin" />
        )}
        {children}
      </button>
    );
  }
);
Button.displayName = "Button";

export { buttonVariants };
